// The reconciliation page (/basket/reconciliation): where a payment link
// sent by the school lands after Stripe. Port of v1's ReconciliationClient:
// the query says expired / cancelled / which payment, then GET
// /widget/app/basket-reconciliation/:paymentId/status?session=<checkout
// session> is asked every 3 seconds, for 30 seconds at most, until it says
// SUCCEEDED or FAILED. Pure.

import { BookingApiError } from "../../api";

export type ReconciliationStatus = "PENDING" | "PROCESSING" | "SUCCEEDED" | "FAILED";

export type ReconciliationPayment = {
  status: ReconciliationStatus;
  /** Pence. */
  amount: number;
  currency: string;
  reason: string;
  schoolName: string;
  basketOrderShortId: string;
  paidAt: string | null;
};

export type ReconciliationQuery =
  | { kind: "expired"; paymentId?: string; session?: string }
  | { kind: "cancelled" }
  | { kind: "not-found" }
  | { kind: "poll"; paymentId: string; session: string; basketOrderId?: string };

export const RECONCILIATION_POLL_MS = 3000;
/** 30 seconds of checks (v1's POLL_MAX_MS), then by hand. */
export const RECONCILIATION_POLL_LIMIT = 10;

/** What the page's query asks for. `status` and `expired` count only when
 * given once (v1 compares them exactly, and a repeated key there was an
 * array); the ids take their first value. */
export function readReconciliationQuery(search: string): ReconciliationQuery {
  const q = new URLSearchParams(search);
  const once = (key: string) => {
    const all = q.getAll(key);
    return all.length === 1 ? all[0] : undefined;
  };
  const paymentId = q.get("payment_id")?.trim() || undefined;
  const session = q.get("checkout_session_id")?.trim() || undefined;
  if (once("expired") === "1") return { kind: "expired", paymentId, session };
  if (once("status") === "cancelled") return { kind: "cancelled" };
  if (!paymentId || !session) return { kind: "not-found" };
  return { kind: "poll", paymentId, session, basketOrderId: q.get("basket_order_id")?.trim() || undefined };
}

export const isSettled = (status: ReconciliationStatus | undefined) => status === "SUCCEEDED" || status === "FAILED";

/** Whether to ask again, after `checks` answers. */
export function keepPolling(payment: ReconciliationPayment | undefined, checks: number): boolean {
  return !isSettled(payment?.status) && checks < RECONCILIATION_POLL_LIMIT;
}

export const STATUS_FAILED = "We couldn't load this payment. Please try again later.";
export const REFRESH_FAILED = "We couldn't send your request. Please contact the school directly.";

/** A failed status check: an unknown payment, or the API's words. */
export function statusFailure(error: unknown): { kind: "not-found" } | { kind: "error"; message: string } {
  if (error instanceof BookingApiError && error.status === 404) return { kind: "not-found" };
  return { kind: "error", message: (error instanceof BookingApiError && error.message) || STATUS_FAILED };
}

export type StatusScreen =
  | { kind: "loading" }
  | { kind: "not-found" }
  | { kind: "error"; message: string }
  | { kind: "payment"; payment: ReconciliationPayment };

/** What the status check shows. Once a payment has been read, it stays on
 * screen: a later check that fails (a blip mid-poll) keeps the last answer
 * rather than replacing it; the error shows only when nothing ever came. */
export function statusScreen(data: ReconciliationPayment | undefined, error: unknown): StatusScreen {
  if (data) return { kind: "payment", payment: data };
  if (error) return statusFailure(error);
  return { kind: "loading" };
}

/** The checks made so far, answered or failed, for the 30-second cap. */
export const checksMade = (s: { dataUpdateCount: number; errorUpdateCount: number }) => s.dataUpdateCount + s.errorUpdateCount;

/** Whether to ask again: after a first answer, until settled or capped,
 * failed checks included. */
export function pollAgain(s: { data?: ReconciliationPayment; dataUpdateCount: number; errorUpdateCount: number }): boolean {
  return !!s.data && keepPolling(s.data, checksMade(s));
}
